/* eslint-disable no-console */
import { FC, useState } from 'react';
import Table from 'components/common/Table/Table';
import { Row as RowProps } from 'react-table';
import { useRecoilValue } from 'recoil';
import { useQuery } from '@apollo/client';
import { SIZE } from 'common/constants';
import ELEMENT from 'common/element';
import { Parser, ParserType, Permission } from 'common/graphqlTypes';
import { hasPermissionState } from 'components/account/account.state';
import { HEADERS } from '../consts';
import { PARSERS_DATA } from '../queries';
import { makeParserRowData } from '../helpers';
import { ParserAndDeselect, ParserData, RowObject, UserRowProp } from '../types';

import CustomParserDialog from '../Dialogs/CustomParserDialog';
import RevertChangesDialog from '../Dialogs/RevertChangesDialog';

type ParsersTableProps = {
  onModifyParser: (parser: string) => void;
};

/**
 *
 * This component represent a table which show list of parsers with their name, type,
 * logs using this parser and list of actions actions
 *
 */
const ParsersTable: FC<ParsersTableProps> = ({ onModifyParser }: ParsersTableProps) => {
  const { data, refetch } = useQuery<ParserData>(PARSERS_DATA);
  const [selectedParser, setSelectedParser] = useState<Parser['id']>('');
  const [customParserDialogIsOpen, setCustomParserDialogIsOpen] = useState(false);
  const [revertChangesDialogIsOpen, setRevertChangesDialogIsOpen] = useState(false);

  const deselectParser: ParserAndDeselect['deselectParser'] = () => {
    setSelectedParser('');
  };

  const onRevertChanges = (parserId: Parser['id']): void => {
    setSelectedParser(parserId);
    setRevertChangesDialogIsOpen(true);
  };

  const onCreateCustomParser = (parserId: Parser['id']): void => {
    setSelectedParser(parserId);
    setCustomParserDialogIsOpen(true);
  };

  const hasFullAccessPermission = useRecoilValue(hasPermissionState(Permission.fullAccess));
  const tableData =
    data?.parsers.map((parser: Parser) =>
      makeParserRowData({
        ...parser,
        isWriteDisabled: !hasFullAccessPermission,
        onRevertChanges,
        onCreateCustomParser,
        onModifyParser,
      }),
    ) || [];

  const tableOptions = {
    isStriped: false,
    isSortable: false,
    hoverRow: true,
    sidePadding: 10,
    size: SIZE.MEDIUM,
  };
  return (
    <>
      <Table
        headers={HEADERS}
        data={tableData}
        options={tableOptions}
        testIdRow={ELEMENT.parsersTableRow}
        userRowProp={(row: RowProps<RowObject>): UserRowProp => {
          const rowItem = row.original;
          return {
            className: rowItem?.typeValue === ParserType.UNDEFINED ? 'ParsersPage__UndefinedParserRow Table__Row' : '',
          };
        }}
      />
      <CustomParserDialog
        selectedParser={selectedParser}
        deselectParser={deselectParser}
        customParserDialogIsOpen={customParserDialogIsOpen}
        setCustomParserDialogIsOpen={setCustomParserDialogIsOpen}
      />
      <RevertChangesDialog
        selectedParser={selectedParser}
        deselectParser={deselectParser}
        revertChangesDialogIsOpen={revertChangesDialogIsOpen}
        setRevertChangesDialogIsOpen={setRevertChangesDialogIsOpen}
        refetchData={refetch}
      />
    </>
  );
};

export default ParsersTable;
